import React from 'react'

export type ViewSectionTone =
  | 'blue'
  | 'teal'
  | 'green'
  | 'sky'
  | 'emerald'
  | 'orange'
  | 'indigo'
  | 'cyan'
  | 'amber'

const TONE: Record<ViewSectionTone, { wrap: string; icon: string; dash: string }> = {
  blue: { wrap: 'bg-blue-50', icon: 'text-blue-600', dash: 'border-blue-200' },
  teal: { wrap: 'bg-teal-50', icon: 'text-teal-600', dash: 'border-teal-200' },
  green: { wrap: 'bg-green-50', icon: 'text-green-600', dash: 'border-green-200' },
  sky: { wrap: 'bg-sky-50', icon: 'text-sky-600', dash: 'border-sky-200' },
  emerald: { wrap: 'bg-emerald-50', icon: 'text-emerald-600', dash: 'border-emerald-200' },
  orange: { wrap: 'bg-orange-50', icon: 'text-orange-600', dash: 'border-orange-200' },
  indigo: { wrap: 'bg-indigo-50', icon: 'text-indigo-600', dash: 'border-indigo-200' },
  cyan: { wrap: 'bg-cyan-50', icon: 'text-cyan-600', dash: 'border-cyan-200' },
  amber: { wrap: 'bg-amber-50', icon: 'text-amber-600', dash: 'border-amber-200' },
}

export interface ViewTabItem<T extends string = string> {
  id: T
  label: string
  full?: string
  accent?: boolean
  count?: number
  icon?: React.ReactNode
}

interface ViewTabBarProps<T extends string> {
  tabs: ViewTabItem<T>[]
  activeTab: T
  onChange: (id: T) => void
  className?: string
  /**
   * Stick below the in-page title bar (`top-[3.25rem]`).
   * Only use on Property-style pages whose sticky header is ~that height.
   * Job/Contract headers are taller, so sticky here overlaps Overview content.
   */
  sticky?: boolean
}

/** In-page tab bar matching Property View. Sticky is opt-in. */
export function ViewTabBar<T extends string>({
  tabs,
  activeTab,
  onChange,
  className = '',
  sticky = false,
}: ViewTabBarProps<T>) {
  return (
    <div
      className={`${sticky ? 'sticky top-[3.25rem] z-10 ' : ''}flex-shrink-0 border-b border-gray-200 bg-white ${className}`.trim()}
    >
      <div className="flex overflow-x-auto scrollbar-none">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={`px-4 sm:px-5 min-h-[44px] text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                isActive
                  ? tab.accent
                    ? 'border-orange-500 text-orange-600'
                    : 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.icon}
              <span className="sm:hidden">{tab.label}</span>
              <span className="hidden sm:inline">{tab.full ?? tab.label}</span>
              {tab.count != null && tab.count > 0 && (
                <span className="ml-0.5 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface ViewSectionProps {
  title: React.ReactNode
  icon?: React.ReactNode
  tone?: ViewSectionTone
  children: React.ReactNode
  actions?: React.ReactNode
  className?: string
}

/** Colored section wrapper matching Property View (icon + h2 + pastel fill). */
export function ViewSection({
  title,
  icon,
  tone = 'blue',
  children,
  actions,
  className = '',
}: ViewSectionProps) {
  const t = TONE[tone]
  return (
    <div className={`${t.wrap} rounded-lg p-6 ${className}`.trim()}>
      <div className={`flex items-center gap-2 mb-4 ${actions ? 'flex-wrap justify-between' : ''}`.trim()}>
        <div className="flex items-center gap-2 min-w-0">
          {icon && <span className={`${t.icon} flex-shrink-0`}>{icon}</span>}
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        </div>
        {actions}
      </div>
      {children}
    </div>
  )
}

interface ViewStatCardProps {
  icon: React.ReactNode
  title: string
  value: React.ReactNode
}

export function ViewStatCard({ icon, title, value }: ViewStatCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-row items-center gap-2 mb-2">
        <span className="inline-flex flex-shrink-0 items-center">{icon}</span>
        <h3 className="font-semibold text-gray-900 leading-none">{title}</h3>
      </div>
      <p className="text-3xl font-bold text-gray-900 leading-tight">{value}</p>
    </div>
  )
}

interface ViewEmptyStateProps {
  icon?: React.ReactNode
  message: string
  tone?: ViewSectionTone
}

export function ViewEmptyState({ icon, message, tone = 'blue' }: ViewEmptyStateProps) {
  const t = TONE[tone]
  return (
    <div className={`bg-white rounded-lg border border-dashed ${t.dash} p-8 text-center`}>
      {icon && <div className="flex justify-center mb-3">{icon}</div>}
      <p className="text-gray-500">{message}</p>
    </div>
  )
}
